import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
gsap.registerPlugin(ScrollToPlugin, ScrollTrigger);
const StickyNav = (): {
  init: () => void;
  refresh: () => void;
  setActiveBtn: (sectionId: string) => void;
} => {
  const nav: HTMLElement | null = document.querySelector(
    '.js-sticky-nav-container',
  );
  let sections: NodeListOf<HTMLElement> = document.querySelectorAll(
    '.js-content-section',
  );
  let navLinks: NodeListOf<HTMLAnchorElement> = nav
    ? nav.querySelectorAll('a')
    : (document.querySelectorAll('a') as NodeListOf<HTMLAnchorElement>);

  const mobileDropdown: HTMLSelectElement | null = document.querySelector(
    '#js-sticky-nav-select',
  );

  const setActiveBtn = (sectionId: string): void => {
    navLinks.forEach(link => {
      link.classList.toggle(
        'active',
        link.getAttribute('href') === `#${sectionId}`,
      );
    });

    // Update the mobile dropdown to match the current section
    if (mobileDropdown) {
      const matchingOption = Array.from(mobileDropdown.options).find(
        option => option.value === `#${sectionId}`,
      );
      if (matchingOption) {
        mobileDropdown.value = `#${sectionId}`; // Update the value without triggering 'change' event
      }
    }
  };

  const initScrollTrigger = (): void => {
    sections.forEach((section: HTMLElement) => {
      ScrollTrigger.create({
        trigger: section,
        start: 'top center',
        end: 'bottom center',
        onEnter: () => setActiveBtn(section.id),
        onEnterBack: () => setActiveBtn(section.id),
      });
    });
  };

  const scrollToSection = (sectionId: string): void => {
    const targetSection: HTMLElement | null =
      document.getElementById(sectionId);

    if (targetSection) {
      gsap.to(window, {
        scrollTo: { y: targetSection, offsetY: 182 },
        duration: 1,
        ease: 'power2.out',
        onComplete: () => setActiveBtn(sectionId), // Ensure button highlighting after scroll
      });
    }
  };

  const handleQueryParamScroll = (): void => {
    const params = new URLSearchParams(window.location.search);
    const sectionId = params.get('st');

    if (sectionId) {
      scrollToSection(sectionId);
    }
  };

  const addClickListeners = (): void => {
    navLinks.forEach(link => {
      link.addEventListener('click', (e: MouseEvent) => {
        e.preventDefault();
        const targetId = link.getAttribute('href')?.replace('#', '') || '';
        if (targetId) {
          scrollToSection(targetId);
        }
      });
    });
  };

  const addDropdownListener = (): void => {
    if (mobileDropdown) {
      mobileDropdown.addEventListener('change', (e: Event) => {
        const selectedValue = (e.target as HTMLSelectElement).value.replace(
          '#',
          '',
        );
        scrollToSection(selectedValue);
      });
    }
  };

  const refresh = (): void => {
    sections = document.querySelectorAll('.js-content-section');
    navLinks = nav
      ? nav.querySelectorAll('a')
      : (document.querySelectorAll('a') as NodeListOf<HTMLAnchorElement>);

    ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    initScrollTrigger();
    ScrollTrigger.refresh();
  };

  const init = (): void => {
    if (!nav || sections.length === 0) return;
    initScrollTrigger();
    addClickListeners();
    addDropdownListener();
    handleQueryParamScroll(); // Scroll based on query parameter
  };

  return { init, refresh, setActiveBtn };
};

export default StickyNav;
